import { useCallback, useMemo } from 'react'

import { useLineChart } from './use-line-chart'

/**
 * The width of the horizontal buffer area to expand hover zone.
 */
const HORIZONTAL_BUFFER = 20

interface Props {
  onActiveIndexChange?: (index: number | null) => void
}

export const LineChartHoverZone: React.FC<Props> = ({ onActiveIndexChange: onActivePointIndexChange }) => {
  const { data, plotAreaWidth = 0, plotAreaHeight, plotAreaPadding } = useLineChart()

  const lineAreaWidth = plotAreaWidth - (plotAreaPadding?.left ?? 0) - (plotAreaPadding?.right ?? 0)
  const spacing = lineAreaWidth / (data.length - 1)

  const points = useMemo(() => {
    return data.map((_, i) => ({
      x: (plotAreaPadding?.left ?? 0) + i * spacing,
    }))
  }, [data, plotAreaPadding?.left, spacing])

  const handleMouseEnter = useCallback(
    (index: number) => {
      onActivePointIndexChange?.(index)
    },
    [onActivePointIndexChange]
  )

  const handleMouseLeave = useCallback(() => {
    onActivePointIndexChange?.(null)
  }, [onActivePointIndexChange])

  return (
    <div className="absolute inset-0 overflow-visible" onMouseLeave={handleMouseLeave}>
      {/* Hover Zones */}
      {data.map((_, i) => {
        const isFirstPoint = i === 0
        const isLastPoint = i === points.length - 1
        const x = (plotAreaPadding?.left ?? 0) + i * spacing
        const x_ = isFirstPoint ? x - HORIZONTAL_BUFFER : x - spacing / 2
        const width = isFirstPoint || isLastPoint ? spacing / 2 + HORIZONTAL_BUFFER : spacing

        return (
          <div
            key={i}
            className="absolute top-0"
            style={{
              left: `${x_}px`,
              width: `${width}px`,
              height: `${plotAreaHeight + (plotAreaPadding?.top ?? 0)}px`,
            }}
            onMouseEnter={() => handleMouseEnter(i)}
          />
        )
      })}
    </div>
  )
}
